// @flow

import * as React from 'react';
import { FormControl, InputLabel, FormHelperText } from '@material-ui/core';
import { TextField, OutlinedInput } from '@material-ui/core';
import { Select, MenuItem, Button } from '@material-ui/core';
import { Box } from 'react-system';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useFormik, Form } from '@realadvisor/form';
import { nth } from '../DevelopmentDetails/utils/nth';
import { UserSingle } from '../../icons/user-single';
import { UserCouple } from '../../icons/user-couple';
import { useLocale } from '../../hooks/locale';
import { useRouter } from '../../controls/router';
import { Link } from '../../controls/link/index';
import { ComboDate } from '../../controls/combo-date';
import { InlineMarked } from '../../controls/marked';
import { RadioCard, RadioCardGroup } from '../../controls/radio-card';
import { BubbleBox, BubblesSeq } from '../../controls/bubble';
import { PriceInput } from '../../controls/price-input';
import { SelectInput } from '../../controls/select-input';
import { LoginContextual } from '../../shared/login/login-contextual';
import type { DevelopmentFirstBuyerStep_root$key } from './__generated__/DevelopmentFirstBuyerStep_root.graphql';
import type { DevelopmentFirstBuyerStep_form$key } from './__generated__/DevelopmentFirstBuyerStep_form.graphql';
import type {
  DevelopmentFirstBuyerStepUpsertFormMutation,
  DevelopmentRegistrationFormInput,
} from './__generated__/DevelopmentFirstBuyerStepUpsertFormMutation.graphql';

type Props = {|
  root: DevelopmentFirstBuyerStep_root$key,
  form: DevelopmentFirstBuyerStep_form$key,
|};

const useOptimisticUpsertForm = ({ form }) => {
  const [upsertForm, upserting] =
    useMutation<DevelopmentFirstBuyerStepUpsertFormMutation>(
      graphql`
        mutation DevelopmentFirstBuyerStepUpsertFormMutation(
          $input: UpsertDevelopmentRegistrationFormInput!
        ) {
          upsertDevelopmentRegistrationForm(input: $input) {
            form: developmentRegistrationForm {
              id
              ...DevelopmentFirstBuyerStep_form @relay(mask: false)
            }
          }
        }
      `,
    );

  const upsertOptimisticForm = (
    input: DevelopmentRegistrationFormInput,
    callback,
  ) => {
    const optimisticResponse = {
      upsertDevelopmentRegistrationForm: {
        form: {
          id: form.id,
          developmentProduct: form.developmentProduct,
          firstBuyerFirstName:
            input.firstBuyerFirstName ?? form.firstBuyerFirstName,
          firstBuyerLastName:
            input.firstBuyerLastName ?? form.firstBuyerLastName,
          firstBuyerDateOfBirth:
            input.firstBuyerDateOfBirth ?? form.firstBuyerDateOfBirth,
          firstBuyerEmailAddress:
            input.firstBuyerEmailAddress ?? form.firstBuyerEmailAddress,
          firstBuyerPhoneNumber:
            input.firstBuyerPhoneNumber ?? form.firstBuyerPhoneNumber,
          firstBuyerAnnualRevenues:
            input.firstBuyerAnnualRevenues ?? form.firstBuyerAnnualRevenues,
          firstBuyerSavings: input.firstBuyerSavings ?? form.firstBuyerSavings,
          numberOfBuyers: input.numberOfBuyers ?? form.numberOfBuyers,
          isAlreadyOwner: input.isAlreadyOwner ?? form.isAlreadyOwner,
          secondBuyerFirstName:
            input.secondBuyerFirstName ?? form.secondBuyerFirstName,
          secondBuyerLastName:
            input.secondBuyerLastName ?? form.secondBuyerLastName,
          product:
            input.productId != null ? { id: input.productId } : form.product,
          step: input.step ?? form.step,
          completed: form.completed,
          phoneNumber: input.phoneNumber ?? form.phoneNumber,
          // cannot be in optimistic response as we manipulate only with ids
          firstBuyerNationality: form.firstBuyerNationality,
          firstBuyerOccupation: form.firstBuyerOccupation,
          firstBuyerWorkPermit: form.firstBuyerWorkPermit,
        },
      },
    };
    upsertForm({
      variables: {
        input: {
          developmentRegistrationForm: {
            id: form.id,
            ...input,
          },
        },
      },
      optimisticResponse,
      onCompleted: () => {
        callback?.();
      },
    });
  };

  return [upsertOptimisticForm, upserting];
};

const string_of_number = (number: ?number) =>
  number != null ? String(number) : '';

const number_of_string = (string: string) => {
  const parsed = Number.parseFloat(string);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
};

const LotSelectionSection = ({ form, completed, onComplete }) => {
  const { t, numberLocale, currency } = useLocale();
  const router = useRouter();
  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const validate = values => {
    const errors = {};

    if (values.productId == null || values.productId === '') {
      errors.productId = t('emptyLotError');
    }

    return errors;
  };

  const f = useFormik({
    initialValues: {
      productId: form.product?.id ?? router.query.productId,
    },
    validate,
    onSubmit: values => {
      optimisticUpsertForm({
        productId: values.productId,
      });
      onComplete();
    },
  });

  const getFloorKey = ({ floorOfFlat, numberOfFloors }) => {
    if (floorOfFlat === 0) {
      return t('floorOfFlat_ground', {
        floorOfFlat: nth(floorOfFlat, t),
      });
    } else if (numberOfFloors === floorOfFlat) {
      return t('floorOfFlat_penthouse', {
        floorOfFlat: nth(floorOfFlat ?? 0, t),
      });
    } else if (typeof floorOfFlat === 'number') {
      return t('floorOfFlat_intermediate', {
        floorOfFlat: nth(floorOfFlat, t),
      });
    }
    return '-';
  };

  return (
    <BubblesSeq animate={completed === false}>
      <BubbleBox>
        <InlineMarked>{t('developmentRegistrationWelcomeBubble')}</InlineMarked>
      </BubbleBox>

      <Form onSubmit={f.submitForm}>
        <Box py={2}>
          <FormControl
            variant="outlined"
            //disabled={disabled}
            error={f.errors.productId != null}
          >
            <InputLabel>
              <InlineMarked>{t('whichLotAreYouInterestedAbout')}</InlineMarked>
            </InputLabel>
            <Select
              value={f.values.productId}
              onChange={e => {
                f.setValues({
                  productId: e.target.value,
                });
                optimisticUpsertForm({
                  productId: e.target.value,
                });
              }}
              input={<OutlinedInput labelWidth={0} />}
              fullWidth={true}
              placeholder={t('pleaseSelectALot')}
            >
              {(form.developmentProduct?.products ?? [])
                .filter(p => p.status === 'available')
                .map(p => {
                  const numberOfRooms = p.property?.numberOfRooms;
                  const floorOfFlat = p.property?.floorOfFlat;

                  return (
                    <MenuItem value={p.id} key={p.id}>
                      {[
                        p.lotReference,
                        p.salePrice != null &&
                          numberLocale.format(Math.round(p.salePrice || 0), {
                            currency: p.currency ?? currency,
                            style: 'currency',
                            maximumFractionDigits: 0,
                            minimumFractionDigits: 0,
                          }),
                        numberOfRooms != null &&
                          `${numberOfRooms} ${t('numberOfRooms')}`,
                        floorOfFlat != null && getFloorKey(p.property),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </MenuItem>
                  );
                })}
            </Select>

            {f.errors.productId != null && (
              <FormHelperText>{f.errors.productId}</FormHelperText>
            )}
          </FormControl>
        </Box>

        {completed === false && (
          <Box py={2}>
            <Button color="primary" size="large" onClick={f.submitForm}>
              {t('next')}
            </Button>
          </Box>
        )}
      </Form>
    </BubblesSeq>
  );
};

const NumberOfBuyersSection = ({ form, me, resetCompleted }) => {
  const { t } = useLocale();
  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const { numberOfBuyers } = form;

  return (
    <BubblesSeq animate={numberOfBuyers == null}>
      <BubbleBox>
        <InlineMarked>
          {t('whoWillBeBuying', { firstName: me.firstName ?? '' })}
        </InlineMarked>
      </BubbleBox>
      <Box>
        <Box py={3}>{t('numberOfBuyersLabel')}</Box>
        <RadioCardGroup>
          <RadioCard
            checked={numberOfBuyers === 1}
            icon={<UserSingle size={48} />}
            onChange={() => {
              resetCompleted();
              optimisticUpsertForm({
                numberOfBuyers: 1,
                secondBuyerFirstName: null,
                secondBuyerLastName: null,
                isAlreadyOwner: null,
                firstBuyerDateOfBirth: null,
                firstBuyerAnnualRevenues: null,
                firstBuyerSavings: null,
                firstBuyerNationalityId: null,
                firstBuyerOccupationId: null,
                firstBuyerWorkPermitId: null,
                step: 'first_buyer',
              });
            }}
          >
            {t('oneBuyer')}
          </RadioCard>
          <RadioCard
            checked={numberOfBuyers === 2}
            icon={<UserCouple size={48} />}
            onChange={() => {
              resetCompleted();
              optimisticUpsertForm({
                numberOfBuyers: 2,
                secondBuyerFirstName: null,
                secondBuyerLastName: null,
                isAlreadyOwner: null,
                firstBuyerDateOfBirth: null,
                firstBuyerAnnualRevenues: null,
                firstBuyerSavings: null,
                firstBuyerNationalityId: null,
                firstBuyerOccupationId: null,
                firstBuyerWorkPermitId: null,
                step: 'first_buyer',
              });
            }}
          >
            {t('twoBuyers')}
          </RadioCard>
        </RadioCardGroup>
      </Box>
    </BubblesSeq>
  );
};

const IsAlreadyOwnerSection = ({ form }) => {
  const { t } = useLocale();

  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const { isAlreadyOwner } = form;

  return (
    <BubblesSeq animate={isAlreadyOwner == null}>
      <BubbleBox>
        <InlineMarked>{t('doYouAlreadyOwnAHome')}</InlineMarked>
      </BubbleBox>
      <Box>
        <RadioCardGroup>
          <RadioCard
            checked={isAlreadyOwner === true}
            onChange={() => {
              optimisticUpsertForm({
                isAlreadyOwner: true,
              });
            }}
          >
            {t('yes')}
          </RadioCard>
          <RadioCard
            checked={isAlreadyOwner === false}
            onChange={() => {
              optimisticUpsertForm({
                isAlreadyOwner: false,
              });
            }}
          >
            {t('no')}
          </RadioCard>
        </RadioCardGroup>
      </Box>
    </BubblesSeq>
  );
};

const SecondBuyerNameSection = ({ form, completed, onComplete }) => {
  const { t } = useLocale();

  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const validate = values => {
    const errors = {};

    if (
      values.secondBuyerFirstName === '' ||
      values.secondBuyerFirstName == null
    ) {
      errors.secondBuyerFirstName = t('emptySecondBuyerFirstError');
    }

    if (
      values.secondBuyerLastName === '' ||
      values.secondBuyerLastName == null
    ) {
      errors.secondBuyerLastName = t('emptySecondBuyerLastNameError');
    }

    return errors;
  };

  const f = useFormik({
    initialValues: {
      secondBuyerFirstName: form.secondBuyerFirstName,
      secondBuyerLastName: form.secondBuyerLastName,
    },
    validate,
    onSubmit: onComplete,
  });

  return (
    <BubblesSeq animate={!completed}>
      <BubbleBox>
        <InlineMarked>{t('giveUsMoreDetailOnYourPartner')}</InlineMarked>
      </BubbleBox>
      <Form onSubmit={f.submitForm}>
        <Box py={2}>
          <FormControl
            variant="outlined"
            error={f.errors.secondBuyerFirstName != null}
          >
            <InputLabel>{t('secondBuyerFirstName')}</InputLabel>
            <TextField
              placeholder={t('firstName')}
              variant="outlined"
              value={f.values.secondBuyerFirstName}
              onChange={e => {
                const value = e.target.value;
                f.setValues({ secondBuyerFirstName: value });
                const valid =
                  validate({ secondBuyerFirstName: value })
                    .secondBuyerFirstName == null;

                optimisticUpsertForm({
                  secondBuyerFirstName: valid ? value : null,
                });
              }}
              onBlur={() => f.setTouched({ secondBuyerFirstName: true })}
            />
            {f.errors.secondBuyerFirstName != null && (
              <FormHelperText>{f.errors.secondBuyerFirstName}</FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box py={2}>
          <FormControl
            variant="outlined"
            error={f.errors.secondBuyerLastName != null}
          >
            <InputLabel>{t('secondBuyerLastName')}</InputLabel>
            <TextField
              placeholder={t('lastName')}
              variant="outlined"
              value={f.values.secondBuyerLastName}
              onChange={e => {
                const value = e.target.value;
                f.setValues({ secondBuyerLastName: value });
                const valid =
                  validate({ secondBuyerLastName: value })
                    .secondBuyerLastName == null;

                optimisticUpsertForm({
                  secondBuyerLastName: valid ? value : null,
                });
              }}
              onBlur={() => f.setTouched({ secondBuyerLastName: true })}
            />
            {f.errors.secondBuyerLastName != null && (
              <FormHelperText>{f.errors.secondBuyerLastName}</FormHelperText>
            )}
          </FormControl>
        </Box>

        {completed === false && (
          <Box py={2}>
            <Button color="primary" size="large" onClick={f.submitForm}>
              {t('next')}
            </Button>
          </Box>
        )}
      </Form>
    </BubblesSeq>
  );
};

const FirstBuyerPersonalInformationSection = ({
  form,
  root,
  completed,
  onComplete,
}) => {
  const { t } = useLocale();
  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const { me } = root;

  const validate = values => {
    const errors = {};

    if (values.firstBuyerDateOfBirth == null) {
      errors.firstBuyerDateOfBirth = t('dateOfBirthError');
    }

    if (values.firstBuyerOccupationId == null) {
      errors.firstBuyerOccupationId = t('occupationError');
    }

    if (values.firstBuyerNationalityId == null) {
      errors.firstBuyerNationalityId = t('nationalityError');
    }

    if (
      values.firstBuyerNationalityId !==
        'TmF0aW9uYWxpdHk6Nzc5OWViYzYtMjZkMS0xMWU4LTgzNGQtZmJiNDg5ZjUxN2Ey' &&
      values.firstBuyerWorkPermitId == null
    ) {
      errors.firstBuyerWorkPermitId = t('workPermiError');
    }

    return errors;
  };

  const f = useFormik({
    initialValues: {
      firstBuyerDateOfBirth: form.firstBuyerDateOfBirth ?? me?.dateOfBirth,
      firstBuyerOccupationId:
        form.firstBuyerOccupation?.id || me?.occupation?.id,
      firstBuyerNationalityId:
        form.firstBuyerNationality?.id || me?.nationality?.id,
      firstBuyerWorkPermitId:
        form.firstBuyerWorkPermit?.id || me?.workPermit?.id,
    },
    validate,
    onSubmit: () => onComplete(),
  });

  // changing select items on every render
  // will trigger controlled selectedItem change
  const occupationTypes = React.useMemo(() => {
    return (root.occupationTypes ?? []).flatMap(item => {
      return item ? [{ id: item.id, label: item.label }] : [];
    });
  }, [root.occupationTypes]);
  const nationalityTypes = React.useMemo(() => {
    return (root.nationalityTypes ?? []).flatMap(item => {
      return item ? [{ id: item.id, label: item.label }] : [];
    });
  }, [root.nationalityTypes]);
  const workPermitTypes = React.useMemo(() => {
    return (root.workPermitTypes ?? []).flatMap(item => {
      return item ? [{ id: item.id, label: item.label }] : [];
    });
  }, [root.workPermitTypes]);

  return (
    <BubblesSeq animate={!completed}>
      <BubbleBox>
        <InlineMarked>
          {t('pleaseGiveUsMoreDetailsOnThefirstBuyer')}
        </InlineMarked>
      </BubbleBox>

      <Form onSubmit={f.submitForm}>
        <Box py={2}>
          <FormControl
            variant="outlined"
            //disabled={disabled}
            error={f.errors.firstBuyerDateOfBirth != null}
          >
            <InputLabel>{t('dateOfBirth')}</InputLabel>
            <ComboDate
              value={f.values.firstBuyerDateOfBirth}
              onChange={value => {
                f.setTouched({ firstBuyerDateOfBirth: true });
                f.setValues({ firstBuyerDateOfBirth: value });
                optimisticUpsertForm({
                  firstBuyerDateOfBirth: value,
                });
              }}
              //disabled={disabled}
            />
            {f.errors.firstBuyerDateOfBirth != null && (
              <FormHelperText>{f.errors.firstBuyerDateOfBirth}</FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box py={2}>
          <FormControl
            variant="outlined"
            //disabled={disabled}
            error={f.errors.firstBuyerOccupationId != null}
          >
            <InputLabel>{t('profession')}</InputLabel>
            <SelectInput
              items={occupationTypes}
              value={f.values.firstBuyerOccupationId}
              onSelect={value => {
                f.setValues({ firstBuyerOccupationId: value });
                optimisticUpsertForm({ firstBuyerOccupationId: value });
              }}
              onBlur={() => f.setTouched({ firstBuyerOccupationId: true })}
            />
            {f.errors.firstBuyerOccupationId != null && (
              <FormHelperText>{f.errors.firstBuyerOccupationId}</FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box py={2}>
          <FormControl
            variant="outlined"
            //disabled={disabled}
            error={f.errors.firstBuyerNationalityId != null}
          >
            <InputLabel>{t('nationality')}</InputLabel>
            <SelectInput
              items={nationalityTypes}
              value={f.values.firstBuyerNationalityId}
              onSelect={value => {
                f.setValues({ firstBuyerNationalityId: value });
                optimisticUpsertForm({ firstBuyerNationalityId: value });
              }}
              onBlur={() => f.setTouched({ firstBuyerNationalityId: true })}
            />
            {f.errors.firstBuyerNationalityId != null && (
              <FormHelperText>
                {f.errors.firstBuyerNationalityId}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        {!['CHE', null].includes(form.firstBuyerNationality?.name) && (
          <Box py={2}>
            <FormControl
              variant="outlined"
              //disabled={disabled}
              error={f.errors.firstBuyerWorkPermitId != null}
            >
              <InputLabel>{t('workPermit')}</InputLabel>
              <SelectInput
                items={workPermitTypes}
                value={f.values.firstBuyerWorkPermitId}
                onSelect={value => {
                  f.setValues({ firstBuyerWorkPermitId: value });
                  optimisticUpsertForm({ firstBuyerWorkPermitId: value });
                }}
                onBlur={() => f.setTouched({ firstBuyerWorkPermitId: true })}
              />
              {f.errors.firstBuyerWorkPermitId != null && (
                <FormHelperText>
                  {f.errors.firstBuyerWorkPermitId}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        )}

        {completed === false && (
          <Box py={2}>
            <Button color="primary" size="large" onClick={f.submitForm}>
              {t('next')}
            </Button>
          </Box>
        )}
      </Form>
    </BubblesSeq>
  );
};

const FirstBuyerAnnualRevenuesSection = ({ form, completed, onComplete }) => {
  const { t } = useLocale();

  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const validate = values => {
    const errors = {};

    if (
      values.firstBuyerAnnualRevenues == null ||
      values.firstBuyerAnnualRevenues === 0
    ) {
      errors.firstBuyerAnnualRevenues = t('emptyRevenuesError');
    }

    return errors;
  };

  const f = useFormik({
    initialValues: {
      firstBuyerAnnualRevenues: form.firstBuyerAnnualRevenues,
    },
    validate,
    onSubmit: onComplete,
  });

  return (
    <BubblesSeq animate={!completed}>
      <BubbleBox>
        <InlineMarked>
          {t('pleaseGiveUsMoreDetailsOnTheFinancialSituationOfThefirstBuyer')}
        </InlineMarked>
      </BubbleBox>

      <Form onSubmit={f.submitForm}>
        <Box py={2}>
          <FormControl
            variant="outlined"
            error={f.errors.firstBuyerAnnualRevenues != null}
          >
            <InputLabel>{t('revenues')}</InputLabel>
            <PriceInput
              value={string_of_number(f.values.firstBuyerAnnualRevenues)}
              onChange={v => {
                const value = number_of_string(v);
                f.setValues({ firstBuyerAnnualRevenues: value });
                const valid =
                  validate({ firstBuyerAnnualRevenues: value })
                    .firstBuyerAnnualRevenues == null;
                optimisticUpsertForm({
                  firstBuyerAnnualRevenues: valid ? value : null,
                });
              }}
              onBlur={() => f.setTouched({ firstBuyerAnnualRevenues: true })}
            />
            {f.errors.firstBuyerAnnualRevenues != null && (
              <FormHelperText>
                {f.errors.firstBuyerAnnualRevenues}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        {completed === false && (
          <Box py={2}>
            <Button color="primary" size="large" onClick={f.submitForm}>
              {t('next')}
            </Button>
          </Box>
        )}
      </Form>
    </BubblesSeq>
  );
};

const FirstBuyerSavingsSection = ({ form }) => {
  const { t } = useLocale();
  const router = useRouter();
  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });
  const validate = values => {
    const errors = {};
    if (values.firstBuyerSavings == null) {
      errors.firstBuyerSavings = t('emptySavingsError');
    }
    return errors;
  };

  const f = useFormik({
    initialValues: {
      firstBuyerSavings: form.firstBuyerSavings,
    },
    validate,
    onSubmit: () => {
      if (form.numberOfBuyers === 1) {
        optimisticUpsertForm({ step: 'verification' }, () => {
          router.push({
            pathname: '/development-registration',
            query: { id: form.id, step: '3' },
          });
        });
      } else {
        optimisticUpsertForm({ step: 'second_buyer' }, () => {
          router.push({
            pathname: '/development-registration',
            query: { id: form.id, step: '2' },
          });
        });
      }
    },
  });

  return (
    <BubblesSeq animate={form.step == null || form.step === 'first_buyer'}>
      <Form onSubmit={f.submitForm}>
        <Box py={2}>
          <FormControl
            variant="outlined"
            error={f.errors.firstBuyerSavings != null}
          >
            <InputLabel>{t('savings')}</InputLabel>
            <PriceInput
              value={string_of_number(f.values.firstBuyerSavings)}
              onChange={v => {
                const value = number_of_string(v);
                f.setValues({ firstBuyerSavings: value });
                const valid =
                  validate({ firstBuyerSavings: value }).firstBuyerSavings ==
                  null;
                optimisticUpsertForm({
                  firstBuyerSavings: valid ? value : null,
                });
              }}
              onBlur={() => f.setTouched({ firstBuyerSavings: true })}
            />
            {f.errors.firstBuyerSavings != null && (
              <FormHelperText>{f.errors.firstBuyerSavings}</FormHelperText>
            )}
          </FormControl>
        </Box>
        <Box py={2}>
          <Button color="primary" size="large" onClick={f.submitForm}>
            {t('next')}
          </Button>
        </Box>
      </Form>
    </BubblesSeq>
  );
};

export const DevelopmentFirstBuyerStep = (props: Props): React.Node => {
  const root = useFragment(
    graphql`
      fragment DevelopmentFirstBuyerStep_root on Query {
        me {
          id
          firstName
          dateOfBirth
          nationality {
            id
            label
            name
          }
          occupation {
            id
            label
          }
          workPermit {
            id
          }
        }
        workPermitTypes: dictionaries(type: "work_permit_types") {
          id
          label
          name
        }
        occupationTypes: dictionaries(type: "occupation_types") {
          id
          label
          name
        }
        nationalityTypes: dictionaries(type: "nationality_types") {
          id
          label
          name
        }
      }
    `,
    props.root,
  );
  const form = useFragment(
    graphql`
      fragment DevelopmentFirstBuyerStep_form on DevelopmentRegistrationForm {
        id
        completed
        phoneNumber
        firstBuyerNationality {
          id
          label
          name
        }
        firstBuyerOccupation {
          id
          label
        }
        firstBuyerWorkPermit {
          id
          label
        }
        firstBuyerFirstName
        firstBuyerLastName
        firstBuyerDateOfBirth
        firstBuyerEmailAddress
        firstBuyerPhoneNumber
        firstBuyerAnnualRevenues
        firstBuyerSavings
        numberOfBuyers
        isAlreadyOwner
        secondBuyerFirstName
        secondBuyerLastName
        step
        product {
          id
        }
        developmentProduct {
          id
          products {
            id
            lotReference
            currency
            salePrice
            status
            property {
              id
              numberOfRooms
              floorOfFlat
              numberOfFloors
            }
          }
        }
      }
    `,
    props.form,
  );

  const me = root.me;

  const [lotSectionCompleted, setLotSectionCompleted] = React.useState(
    form.product?.id != null,
  );

  const [secondBuyerNameSectionCompleted, setSecondBuyerNameSectionCompleted] =
    React.useState(
      form.secondBuyerFirstName != null && form.secondBuyerLastName != null,
    );

  const [
    firstBuyerPersonalInformationSectionCompleted,
    setFirstBuyerPersonalInformationSectionCompleted,
  ] = React.useState(form.firstBuyerAnnualRevenues != null);

  const [
    firstBuyerAnnualRevenuesSectionCompleted,
    setFirstBuyerAnnualRevenuesSectionCompleted,
  ] = React.useState(form.firstBuyerAnnualRevenues != null);
  const isAlreadyOwnerComplete = form.isAlreadyOwner != null;

  const resetCompleted = () => {
    setSecondBuyerNameSectionCompleted(false);
    setFirstBuyerPersonalInformationSectionCompleted(false);
    setFirstBuyerAnnualRevenuesSectionCompleted(false);
  };

  return (
    <>
      <LotSelectionSection
        form={form}
        completed={lotSectionCompleted}
        onComplete={() => setLotSectionCompleted(true)}
      />

      {me != null && lotSectionCompleted && (
        <NumberOfBuyersSection
          form={form}
          me={me}
          resetCompleted={resetCompleted}
        />
      )}

      {!me && lotSectionCompleted && (
        <Link
          href={{
            pathname: '/development-registration',
            query: { id: form.id, step: '1' },
          }}
        >
          {({ href }) => (
            <LoginContextual
              privacyAgreement={null}
              communicationOptIn={null}
              returnHref={href}
            />
          )}
        </Link>
      )}

      {form.numberOfBuyers === 2 && (
        <SecondBuyerNameSection
          form={form}
          root={root}
          completed={secondBuyerNameSectionCompleted}
          onComplete={() => {
            setSecondBuyerNameSectionCompleted(true);
          }}
        />
      )}

      {(form.numberOfBuyers === 1 ||
        (form.numberOfBuyers === 2 && secondBuyerNameSectionCompleted)) && (
        <IsAlreadyOwnerSection form={form} />
      )}

      {isAlreadyOwnerComplete && (
        <FirstBuyerPersonalInformationSection
          root={root}
          form={form}
          completed={firstBuyerPersonalInformationSectionCompleted}
          onComplete={() => {
            setFirstBuyerPersonalInformationSectionCompleted(true);
          }}
        />
      )}

      {firstBuyerPersonalInformationSectionCompleted && (
        <FirstBuyerAnnualRevenuesSection
          form={form}
          root={root}
          completed={firstBuyerAnnualRevenuesSectionCompleted}
          onComplete={() => {
            setFirstBuyerAnnualRevenuesSectionCompleted(true);
          }}
        />
      )}

      {firstBuyerAnnualRevenuesSectionCompleted && (
        <FirstBuyerSavingsSection form={form} />
      )}
    </>
  );
};
